import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Download, Printer, Search, Edit2, Trash2, X, XCircle, Check, RefreshCw, CreditCard, Package, Receipt, Truck, FileText, Eye } from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { BillPrintPreview } from '../components/BillPrintPreview';
// EditBillModal removed — Edit now happens inline via loadBillForEdit().
import InvoiceModal from '../components/InvoiceModal';
import AIMemoryBanner from '../components/AIMemoryBanner';
import AddToPriceListButton from '../components/AddToPriceListButton';
import ProductCodeBadge from '../components/ProductCodeBadge';
import ProductCombobox from '../components/ProductCombobox';
import MobileModal from '../components/MobileModal';
import { useProductCodes } from '../context/ProductCodesContext';
import EnterpriseBillHistoryTab from '../components/enterprise/EnterpriseBillHistoryTab';
import EnterpriseDispatchTab from '../components/enterprise/EnterpriseDispatchTab';

// Indian-number to words — "Rupees X Only"
function amountToWords(num) {
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (n) => n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
  const three = (n) => {
    const h = Math.floor(n / 100); const r = n % 100;
    return (h ? a[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? two(r) : '');
  };
  const n = Math.floor(num || 0);
  if (n === 0) return 'Zero';
  let out = '';
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thou = Math.floor((n % 100000) / 1000);
  const hund = n % 1000;
  if (crore) out += two(crore) + ' Crore ';
  if (lakh) out += two(lakh) + ' Lakh ';
  if (thou) out += two(thou) + ' Thousand ';
  if (hund) out += three(hund);
  const paise = Math.round(((num || 0) - n) * 100);
  if (paise) out += ' and ' + two(paise) + ' Paise';
  return out.trim() + ' Only';
}

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'NEFT', 'RTGS', 'IMPS'];

const EMPTY_ITEM = { product_id: '', product_name: '', quantity: '', rate: '', unit: 'KG', amount: 0 };

// ─────────────────────────────────────────────────────────────
// Download via fetch + blob. Works on preview/production cross-origin
// because backend CORS allows credentials & wildcard origin.
// Falls back to opening in a new tab if the fetch fails.
// ─────────────────────────────────────────────────────────────
const downloadPdf = async (url, filename) => {
  const toastId = toast.loading('Preparing PDF…');
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'invoice.pdf';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }, 1000);
    toast.success('Downloaded', { id: toastId });
  } catch (err) {
    toast.dismiss(toastId);
    // Fallback — open in a new tab so user can Save As
    try { window.open(url, '_blank', 'noopener'); } catch {}
    toast.error('Could not auto-download. Opening in new tab to Save As.');
  }
};

// ─────────────────────────────────────────────────────────────
// Payment Modal
// ─────────────────────────────────────────────────────────────
function PaymentModal({ bill, onClose, onSaved }) {
  const outstanding = Math.max(0, (parseFloat(bill.total_amount) || 0) - (parseFloat(bill.paid_amount) || 0));
  const [form, setForm] = useState({ amount: outstanding, method: 'Cash', date: new Date().toISOString().slice(0, 10), note: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/invoices/${bill.id}/payment`, form, { withCredentials: true });
      toast.success(`Payment of Rs. ${fmt(form.amount)} recorded`);
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setSaving(false); }
  };

  return (
    <MobileModal onClose={onClose} maxWidth="md" testId="payment-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-zinc-50 font-heading font-semibold text-base">Record Payment</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{bill.invoice_number} · {bill.client}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-center">
              <div className="text-zinc-500 text-[10px]">Total</div>
              <div className="text-zinc-200 font-mono font-bold text-sm">Rs. {fmt(bill.total_amount)}</div>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-center">
              <div className="text-zinc-500 text-[10px]">Paid</div>
              <div className="text-green-400 font-mono font-bold text-sm">Rs. {fmt(bill.paid_amount || 0)}</div>
            </div>
            <div className="bg-red-950/30 border border-red-900/40 rounded-lg p-3 text-center">
              <div className="text-red-500 text-[10px]">Outstanding</div>
              <div className="text-red-400 font-mono font-bold text-sm">Rs. {fmt(outstanding)}</div>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Amount Received *</label>
            <input type="number" min="0" step="any" autoFocus value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="payment-amount-input"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-amber-400 font-mono text-lg focus:outline-none focus:border-amber-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Method</label>
              <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                {PAYMENT_METHODS.map(m => <option key={m} value={m} className="bg-zinc-900">{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Note (optional)</label>
            <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="UTR, cheque no, reference..." data-testid="payment-note-input"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-200 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm font-medium">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="save-payment-btn"
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1.5">
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
              Record Payment
            </button>
          </div>
        </div>
    </MobileModal>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Enterprise page
// ─────────────────────────────────────────────────────────────
export default function Enterprise() {
  const [tab, setTab] = useState('new');
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [bills, setBills] = useState([]);
  const [dispatch, setDispatch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchBill, setSearchBill] = useState('');
  const [searchDispatch, setSearchDispatch] = useState('');
  const [filterPayment, setFilterPayment] = useState('All');
  const [filterInvType, setFilterInvType] = useState('All');
  const [payBill, setPayBill] = useState(null);
  const [previewBill, setPreviewBill] = useState(null);
  const [editBill, setEditBill] = useState(null);
  const [shareBill, setShareBill] = useState(null);  // opens InvoiceModal (WhatsApp / Email / Attachments)
  // Bill History sort — default invoice number DESC (newest invoice on top)
  const [billSortKey, setBillSortKey] = useState('invoice_number');
  const [billSortDir, setBillSortDir] = useState('desc');
  const toggleBillSort = (k) => {
    if (billSortKey === k) setBillSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setBillSortKey(k); setBillSortDir('desc'); }
  };

  // Invoice form state
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [billNoOverride, setBillNoOverride] = useState('');
  const [buyerId, setBuyerId] = useState('');
  const [buyer, setBuyer] = useState({ name: '', address: '', phone: '', email: '', state: 'Maharashtra' });
  const [clientPriceList, setClientPriceList] = useState([]);
  const [dispatchThrough, setDispatchThrough] = useState('');
  const [destination, setDestination] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [otherRef, setOtherRef] = useState('');
  const [despatchNo, setDespatchNo] = useState('');
  const [termsOfDelivery, setTermsOfDelivery] = useState('');
  const [narration, setNarration] = useState('');
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  // Extra charges (transport, packing, loading, unloading, etc.) — appended to the bill total.
  // Each row: {label, amount}. Rendered as additional non-item lines in the totals block.
  const [extraCharges, setExtraCharges] = useState([]);
  // Autosave key — namespaced per client so switching debtors loads their last draft.
  const draftKey = useMemo(() => `enterprise-draft-${buyerId || 'unsaved'}`, [buyerId]);

  // SECURITY NOTE: the autosave draft below uses localStorage. This stores
  // ONLY transient bill-in-progress data (line items, narration, extra
  // charges) the operator is actively typing — no auth tokens, no PII for
  // *other* users (key is namespaced per buyer ID, and login is via httpOnly
  // cookie). If your threat model includes shared/kiosk devices, log out
  // wipes this draft on the next session via the 7-day TTL gate below.

  // Restore unsaved draft when client changes
  useEffect(() => {
    if (!buyerId || editBill) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      // Only restore if draft is < 7 days old (avoid stale autofill)
      const age = Date.now() - (draft.savedAt || 0);
      if (age > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(draftKey);
        return;
      }
      if (Array.isArray(draft.items) && draft.items.length > 0) setItems(draft.items);
      if (Array.isArray(draft.extraCharges)) setExtraCharges(draft.extraCharges);
      if (draft.narration) setNarration(draft.narration);
      toast.info(`Restored unsaved draft from ${new Date(draft.savedAt).toLocaleString()}`, { duration: 4000 });
    } catch { /* ignore corrupt draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, editBill]);

  // Persist draft on every meaningful change (debounced via setTimeout)
  useEffect(() => {
    if (!buyerId || editBill) return;
    const hasContent = items.some(it => (it.product_id || it.product_name) && parseFloat(it.quantity) > 0)
      || extraCharges.some(c => c.label && parseFloat(c.amount) > 0);
    if (!hasContent) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          items, extraCharges, narration, savedAt: Date.now(),
        }));
      } catch { /* quota exceeded — ignore */ }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, extraCharges, narration, buyerId, editBill]);

  const fetchData = async () => {
    try {
      const [cRes, fgRes, rmRes, bRes, dRes] = await Promise.all([
        axios.get(`${API}/clients/`, { withCredentials: true }),
        axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
        axios.get(`${API}/invoices/bill-log?invoice_type=Non-GST`, { withCredentials: true }),
        axios.get(`${API}/invoices/dispatch/register`, { withCredentials: true }),
      ]);
      setClients(cRes.data.filter(c => (c.type === 'Debtor' || c.type === 'Both') && !(c.gstin && c.gstin.trim())));
      // Merge FG + RM into one product list. Each row is tagged with `item_type`
      // so the line-item submit + stock-revert logic can route to the right collection.
      const fgList = (fgRes.data || []).map(p => ({ ...p, item_type: 'finished' }));
      const rmList = (rmRes.data || []).map(p => ({
        ...p,
        item_type: 'raw',
        // RMs use `current_rate` not `price_per_unit` — surface a sane default rate
        price_per_unit: p.current_rate || p.price_per_unit || 0,
      }));
      setProducts([...fgList, ...rmList]);
      setBills(bRes.data);
      setDispatch(dRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchData);
  const { lookupProduct } = useProductCodes();

  useEffect(() => { fetchData(); }, []);

  // Auto-prefill from a Sales Order: /enterprise?from_so=so-xxxx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const soId = params.get('from_so');
    if (!soId || clients.length === 0 || products.length === 0) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/sales/orders`, { withCredentials: true });
        const so = (res.data || []).find(o => o.id === soId);
        if (!so) { toast.error('Sales order not found'); return; }
        setTab('new');
        setInvoiceDate((so.created_at || new Date().toISOString()).slice(0, 10));
        // Find matching debtor or populate raw buyer fields
        const matched = clients.find(c => c.name === so.client);
        if (matched) {
          setBuyerId(matched.id);
          setBuyer({
            name: matched.name,
            address: [matched.address, matched.city, matched.state].filter(Boolean).join(', '),
            phone: matched.phone || '',
            email: matched.email || '',
            state: matched.state || 'Maharashtra',
          });
          try {
            const pl = await axios.get(`${API}/clients/${matched.id}/price-list`, { withCredentials: true });
            setClientPriceList(pl.data || []);
          } catch { /* ignore */ }
        } else {
          setBuyer({
            name: so.client || '',
            address: so.client_address || '',
            phone: so.client_phone || '',
            email: so.client_email || '',
            state: so.client_state || 'Maharashtra',
          });
        }
        // Items: prefer multi-line items[]; fallback to legacy single-item
        const rows = (so.items && so.items.length > 0) ? so.items : [{
          product_id: so.product_id, product_name: so.product_name,
          quantity: so.quantity, rate: so.unit_price, unit: so.unit || 'KG',
        }];
        setItems(rows.map(it => {
          const p = products.find(pr => pr.id === it.product_id || pr.name === it.product_name);
          const qty = parseFloat(it.qty || it.quantity || 0);
          const rate = parseFloat(it.rate || it.unit_price || 0);
          return {
            product_id: p?.id || it.product_id || '',
            product_name: it.product_name || p?.name || '',
            quantity: qty,
            rate,
            unit: it.unit || p?.unit || 'KG',
            amount: Math.round(qty * rate * 100) / 100,
            has_custom_price: false,
          };
        }));
        // Transport / references
        setDispatchThrough(so.dispatch_through || '');
        setDestination(so.destination || '');
        setDeliveryNote(so.delivery_note_date || '');
        setPaymentTerms(so.payment_terms || '');
        setSupplierRef(so.supplier_ref || so.voucher_no || '');
        setOtherRef(so.other_ref || '');
        setDespatchNo(so.dispatch_id || '');
        setTermsOfDelivery(so.terms_of_delivery || '');
        setNarration(so.narration || so.remark || so.notes || '');
        toast.success(`Auto-filled from ${so.voucher_no || so.id.slice(0, 8)}`);
        // Strip the query param so a refresh doesn't re-run
        const url = new URL(window.location.href);
        url.searchParams.delete('from_so');
        window.history.replaceState({}, '', url);
      } catch (e) { console.error(e); toast.error('Failed to auto-fill from SO'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients.length, products.length]);

  const resetForm = () => {
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setBillNoOverride('');
    setBuyerId('');
    setBuyer({ name: '', address: '', phone: '', email: '', state: 'Maharashtra' });
    setClientPriceList([]);
    setDispatchThrough(''); setDestination(''); setDeliveryNote(''); setPaymentTerms('');
    setSupplierRef(''); setOtherRef(''); setDespatchNo(''); setTermsOfDelivery(''); setNarration('');
    setItems([{ ...EMPTY_ITEM }]);
  };

  const selectBuyer = async (clientId) => {
    setBuyerId(clientId);
    if (!clientId) {
      setBuyer({ name: '', address: '', phone: '', email: '', state: 'Maharashtra' });
      setClientPriceList([]);
      return;
    }
    const c = clients.find(cl => cl.id === clientId);
    if (c) {
      setBuyer({
        name: c.name,
        address: [c.address, c.city, c.state].filter(Boolean).join(', '),
        phone: c.phone || '',
        email: c.email || '',
        state: c.state || 'Maharashtra',
      });
      // Auto-fill dispatch & reference details from the client's saved
      // defaults (configured in Debtors → Edit). Only overwrites blank
      // fields so an operator's per-bill edits aren't clobbered.
      if (c.default_dispatch_address && !destination) setDestination(c.default_dispatch_address);
      if (c.default_transport_mode && !dispatchThrough) setDispatchThrough(c.default_transport_mode);
      if (c.default_transporter_name && !dispatchThrough) setDispatchThrough(c.default_transporter_name);
      if (c.default_vehicle_no && !despatchNo) setDespatchNo(c.default_vehicle_no);
      if (c.default_reference_prefix && !supplierRef) setSupplierRef(c.default_reference_prefix);
      if (c.default_payment_terms && !paymentTerms) setPaymentTerms(c.default_payment_terms);
      if (c.default_eway_bill_required && !termsOfDelivery) setTermsOfDelivery('E-Way Bill required');

      // Fetch custom price list for this debtor
      try {
        const res = await axios.get(`${API}/clients/${clientId}/price-list`, { withCredentials: true });
        setClientPriceList(res.data || []);
        if ((res.data || []).length > 0) {
          toast.success(`${res.data.length} custom price${res.data.length > 1 ? 's' : ''} loaded for ${c.name}`);
        }
      } catch { setClientPriceList([]); }
    }
  };

  const selectProduct = (i, productId) => {
    const p = products.find(pr => pr.id === productId);
    if (!p) return;
    // Check for custom price in debtor's price list
    const custom = clientPriceList.find(cp => cp.product_id === productId);
    const rate = custom?.rate ?? p.price_per_unit ?? p.current_rate ?? 0;
    const unit = custom?.unit || p.unit || 'KG';
    const newItems = [...items];
    const qty = parseFloat(newItems[i].quantity) || 0;
    newItems[i] = {
      ...newItems[i],
      product_id: p.id,
      product_name: p.name,
      product_code: p.product_code || lookupProduct?.(p.name) || newItems[i].product_code || '',
      item_type: p.item_type || 'finished',  // 'finished' (FG) or 'raw' (RM)
      rate,
      unit,
      amount: Math.round(qty * rate * 100) / 100,
      has_custom_price: !!custom,
    };
    setItems(newItems);
  };

  const updateItem = (i, field, value) => {
    const newItems = [...items];
    newItems[i] = { ...newItems[i], [field]: value };
    if (field === 'quantity' || field === 'rate') {
      const q = parseFloat(newItems[i].quantity || 0);
      const r = parseFloat(newItems[i].rate || 0);
      newItems[i].amount = Math.round(q * r * 100) / 100;
    }
    setItems(newItems);
  };

  const addItemRow = () => setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  const removeItemRow = (i) => setItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const subtotal = items.reduce((s, it) => s + (it.amount || 0), 0);
  // Extra charges total (transport, packing, loading, unloading, custom)
  const extraTotal = extraCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const grandTotal = subtotal + extraTotal;
  const totalQty = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);

  // Hydrate the Enterprise form with an existing bill so the operator edits
  // it INSIDE the same page (no modal overlay). Triggered by setEditBill on
  // a row in Bill History. On Submit, we PUT instead of POST.
  const loadBillForEdit = async (bill) => {
    try {
      const res = await axios.get(`${API}/invoices/${bill.id}`, { withCredentials: true });
      const o = res.data;
      setInvoiceDate((o.invoice_date || o.created_at || new Date().toISOString()).slice(0, 10));
      setBillNoOverride(o.invoice_number || '');
      setBuyerId(o.client_id || '');
      setBuyer({
        name: o.client || '',
        address: o.client_address || '',
        phone: o.client_phone || '',
        email: o.client_email || '',
        state: o.client_state || 'Maharashtra',
      });
      setDispatchThrough(o.dispatch_through || '');
      setDestination(o.destination || '');
      setDeliveryNote(o.lr_no || '');
      setPaymentTerms(o.payment_terms || '');
      setSupplierRef(o.supplier_ref || '');
      setOtherRef(o.other_ref || '');
      setDespatchNo(o.dispatch_id || '');
      setTermsOfDelivery(o.terms_of_delivery || '');
      setNarration(o.narration || '');
      const loadedItems = (o.items && o.items.length > 0)
        ? o.items.map(it => ({
            product_id: it.product_id || '',
            product_name: it.product_name || '',
            item_type: it.item_type || 'finished',
            quantity: it.quantity || 0,
            rate: it.rate || it.unit_price || 0,
            amount: (parseFloat(it.quantity || 0) * parseFloat(it.rate || it.unit_price || 0)) || 0,
            unit: it.unit || 'KG',
          }))
        : [{ product_id: o.product_id || '', product_name: o.product_name || '', item_type: 'finished',
              quantity: o.quantity || 0, rate: o.unit_price || 0,
              amount: (parseFloat(o.quantity || 0) * parseFloat(o.unit_price || 0)) || 0,
              unit: 'KG' }];
      setItems(loadedItems);
      setExtraCharges(o.extra_charges || []);
      setEditBill({ id: bill.id, invoice_number: o.invoice_number });
      setTab('new');
      // Smooth scroll to the top of the create form
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    } catch (e) {
      toast.error('Failed to load bill: ' + (e.response?.data?.detail || e.message));
    }
  };

  const cancelEdit = () => {
    setEditBill(null);
    resetForm();
    setTab('history');
  };

  const handleSubmit = async () => {
    if (!buyer.name) { toast.error('Select a buyer'); return; }
    const validItems = items.filter(it => (it.product_name || it.product_id) && parseFloat(it.quantity) > 0);
    if (!validItems.length) { toast.error('Add at least one item with quantity'); return; }

    // Negative-stock detection — any item with qty > current_stock will be
    // recorded as a stock-debt by the backend (which auto-fulfills when the
    // next production batch of that product completes). No confirm dialog —
    // operators were getting blocked by browser modal pop-ups inside the
    // embedded preview; the inline yellow banner above the Save button
    // already warns them, and the toast on success confirms how many debts
    // were recorded.
    const shortages = validItems.map(it => {
      const p = products.find(pr => pr.id === it.product_id || pr.name === it.product_name);
      const stock = parseFloat(p?.current_stock || 0);
      const qty = parseFloat(it.quantity || 0);
      if (qty > stock) return { name: p?.name || it.product_name, shortfall: qty - stock, unit: p?.unit || 'kg' };
      return null;
    }).filter(Boolean);

    setSubmitting(true);
    try {
      const payload = {
        voucher_type: 'sales_invoice',
        date: invoiceDate,
        party_name: buyer.name,
        party_id: buyerId,
        gst_enabled: false,
        items: validItems.map(it => ({
          product_id: it.product_id,
          product_name: it.product_name,
          item_type: it.item_type || 'finished',  // tells backend whether to deduct from RM or FG
          quantity: parseFloat(it.quantity),
          rate: parseFloat(it.rate),
          unit: it.unit,
        })),
        client_address: buyer.address,
        client_phone: buyer.phone,
        client_state: buyer.state,
        dispatch_through: dispatchThrough,
        destination,
        lr_no: deliveryNote,
        payment_terms: paymentTerms,
        supplier_ref: supplierRef,
        other_ref: otherRef,
        dispatch_id: despatchNo,
        terms_of_delivery: termsOfDelivery,
        narration,
        override_negative_stock: shortages.length > 0,  // signals backend to record a stock_debt
      };
      if (billNoOverride) payload.bill_no_override = billNoOverride;
      // Persist extra charges (transport, packing, loading, etc.) at voucher level
      payload.extra_charges = extraCharges
        .filter(c => c.label && parseFloat(c.amount) > 0)
        .map(c => ({ label: c.label, amount: parseFloat(c.amount) || 0 }));

      let resultOrderId, resultInvNum;
      if (editBill) {
        // EDIT MODE — call PUT /sales/orders/{id} to amend the existing
        // bill in place. The backend recomputes totals and keeps the
        // voucher/cashflow row in sync.
        const editPayload = {
          client: buyer.name,
          client_id: buyerId,
          client_address: buyer.address,
          client_phone: buyer.phone,
          client_state: buyer.state,
          items: payload.items,
          extra_charges: payload.extra_charges,
          dispatch_through: dispatchThrough,
          destination,
          lr_no: deliveryNote,
          payment_terms: paymentTerms,
          supplier_ref: supplierRef,
          other_ref: otherRef,
          dispatch_id: despatchNo,
          terms_of_delivery: termsOfDelivery,
          narration,
          invoice_date: invoiceDate,
        };
        // iter222 — When the user types a new bill number during edit,
        // push it through as invoice_number so the backend cascade helper
        // rewrites every downstream reference (linked voucher, receipt
        // vouchers' against_bills, cashflow rows, ledger). Admin/Owner
        // only on the backend; a 403 or 409 (duplicate) surfaces here.
        const trimmedOverride = (billNoOverride || '').trim();
        if (trimmedOverride && trimmedOverride !== (editBill.invoice_number || '').trim()) {
          editPayload.invoice_number = trimmedOverride;
        }
        const res = await axios.put(`${API}/sales/orders/${editBill.id}`, editPayload, { withCredentials: true });
        resultOrderId = res.data.id || editBill.id;
        resultInvNum = res.data.invoice_number || editBill.invoice_number;
        const cascade = res.data._invoice_number_cascade;
        if (cascade && cascade.old && cascade.new && cascade.old !== cascade.new) {
          const touched =
            (cascade.vouchers_header || 0) +
            (cascade.vouchers_against_bills || 0) +
            (cascade.transactions_against_bills || 0) +
            (cascade.transactions_description || 0);
          toast.success(`Invoice renamed → ${cascade.new} · ${touched} linked record(s) synced`);
        } else {
          toast.success(`Invoice ${resultInvNum} updated`);
        }
      } else {
        const res = await axios.post(`${API}/vouchers/`, payload, { withCredentials: true });
        const sharp = shortages.length > 0 ? ` · ${shortages.length} stock debt(s) recorded` : '';
        toast.success(`Invoice ${res.data.invoice_number || ''} created · Stock auto-deducted${sharp}`);
        resultOrderId = res.data.linked_order_id || `vinv-${String(res.data.id || '').slice(0, 8)}`;
        resultInvNum = res.data.invoice_number || '';
      }

      // Clear autosaved draft once the bill is committed
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      setExtraCharges([]);
      const wasEditing = !!editBill;
      setEditBill(null);
      resetForm();
      await fetchData();
      // "Generate" half of Save & Generate — immediately open the print preview
      // for the freshly-created (or just-edited) bill.
      if (resultOrderId && !wasEditing) {
        setPreviewBill({ id: resultOrderId, invoice_number: resultInvNum });
      } else {
        setTab('history');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  // Filtering + sort (default invoice number DESC — newest on top per spec)
  const filteredBills = (() => {
    const seq = (s) => parseInt(((s || '').match(/(\d+)$/) || ['0', '0'])[1], 10);
    const cmp = (a, b) => {
      let va, vb;
      switch (billSortKey) {
        case 'invoice_number': va = seq(a.invoice_number); vb = seq(b.invoice_number); break;
        case 'client':         va = (a.client || '').toLowerCase(); vb = (b.client || '').toLowerCase(); break;
        case 'total':          va = parseFloat(a.total_amount || 0); vb = parseFloat(b.total_amount || 0); break;
        case 'paid':           va = parseFloat(a.paid_amount || 0); vb = parseFloat(b.paid_amount || 0); break;
        case 'outstanding':    va = Math.max(0, parseFloat(a.total_amount || 0) - parseFloat(a.paid_amount || 0)); vb = Math.max(0, parseFloat(b.total_amount || 0) - parseFloat(b.paid_amount || 0)); break;
        case 'payment_status': va = (a.payment_status || 'Unpaid').toLowerCase(); vb = (b.payment_status || 'Unpaid').toLowerCase(); break;
        case 'date':
        default:               va = a.invoice_date || a.created_at || ''; vb = b.invoice_date || b.created_at || ''; break;
      }
      if (va < vb) return billSortDir === 'asc' ? -1 : 1;
      if (va > vb) return billSortDir === 'asc' ?  1 : -1;
      return 0;
    };
    return bills.filter(b => {
      if (searchBill && !(b.client || '').toLowerCase().includes(searchBill.toLowerCase())
        && !(b.invoice_number || '').toLowerCase().includes(searchBill.toLowerCase())) return false;
      if (filterPayment !== 'All' && b.payment_status !== filterPayment) return false;
      return true;
    }).sort(cmp);
  })();

  const filteredDispatch = dispatch.filter(d => {
    if (filterInvType !== 'All' && d.invoice_type !== filterInvType) return false;
    if (!searchDispatch) return true;
    const s = searchDispatch.toLowerCase();
    return (d.client || '').toLowerCase().includes(s)
      || (d.invoice_number || '').toLowerCase().includes(s)
      || (d.batch_ids || []).some(b => (b || '').toLowerCase().includes(s))
      || (d.items || []).some(it => (it.product_name || '').toLowerCase().includes(s));
  });

  if (loading) return <div className="text-zinc-400 animate-pulse p-8">Loading Enterprise…</div>;

  return (
    <div className="space-y-4" data-testid="enterprise-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Enterprise · Non-GST Billing</h1>
          <p className="text-zinc-500 text-xs mt-0.5">Tirth Enterprises (Non-GST) · Stock auto-deducts on save · Batch IDs tracked in Dispatch Register</p>
        </div>
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-xl p-0.5 gap-0.5">
          {[
            ['new', 'New Invoice', Receipt],
            ['history', `Bill History (${bills.length})`, FileText],
            ['dispatch', `Dispatch (${dispatch.length})`, Truck],
          ].map(([key, label, Icon]) => (
            <button key={key} onClick={() => { if (key === 'new' && !editBill) resetForm(); setTab(key); }}
              data-testid={`tab-${key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${tab === key ? 'bg-amber-500 text-black font-bold' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <Icon size={12} />{label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ NEW INVOICE TAB ══ */}
      {tab === 'new' && (
        <div className="space-y-4" data-testid="enterprise-invoice-form">
          {editBill && (
            <div className="bg-blue-500/10 border border-blue-500/40 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2" data-testid="edit-mode-banner">
              <div className="text-blue-300 text-xs">
                <span className="font-bold">Editing existing bill</span>
                {editBill.invoice_number && <span className="ml-2 text-blue-200 font-mono">{editBill.invoice_number}</span>}
                <span className="ml-2 text-blue-400/70">— changes will be saved to this bill on click of <span className="font-semibold text-blue-200">Save Changes</span></span>
              </div>
              <button onClick={cancelEdit} className="text-[11px] text-zinc-400 hover:text-zinc-200 underline">Cancel</button>
            </div>
          )}
          {/* Meta row */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider">Bill Date *</label>
              <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                className="mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-xs font-mono focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider">Bill Number</label>
              <input value={billNoOverride} onChange={e => setBillNoOverride(e.target.value)} placeholder="Auto-generated"
                data-testid="bill-no-input"
                className="mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-amber-400 text-xs font-mono focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider">Billing Entity</label>
              <div className="mt-1 flex items-center gap-2 px-2.5 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
                <Receipt size={11} /> Tirth Enterprises (Non-GST)
              </div>
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider">Payment Terms</label>
              <select value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)}
                className="mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500">
                <option value="">—</option>
                {['Cash', 'Net 7', 'Net 15', 'Net 30', 'Advance'].map(t => <option key={t} value={t} className="bg-zinc-900">{t}</option>)}
              </select>
            </div>
          </div>

          {/* Buyer row */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-zinc-200 text-sm font-semibold">Buyer (Debtor)</h3>
              {clientPriceList.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300 border border-blue-700" data-testid="custom-prices-badge">
                  {clientPriceList.length} custom price{clientPriceList.length > 1 ? 's' : ''} applied
                </span>
              )}
              <div className="flex-1" />
              <span className="text-zinc-600 text-[10px]">{clients.length} Non-GST debtors</span>
            </div>
            {buyer.name && <AIMemoryBanner client={buyer.name} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-500 text-[10px] mb-1 block">Select Client *</label>
                <select value={buyerId} onChange={e => selectBuyer(e.target.value)} data-testid="buyer-select"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                  <option value="">— Select Non-GST debtor —</option>
                  {clients.map(c => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}{c.city ? ` · ${c.city}` : ''}</option>)}
                </select>
                <input value={buyer.name} onChange={e => setBuyer(b => ({ ...b, name: e.target.value }))}
                  placeholder="Or type buyer name"
                  className="mt-2 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="space-y-2">
                <input value={buyer.address} onChange={e => setBuyer(b => ({ ...b, address: e.target.value }))}
                  placeholder="Address (auto-filled)" data-testid="buyer-address"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={buyer.phone} onChange={e => setBuyer(b => ({ ...b, phone: e.target.value }))}
                    placeholder="Phone"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500" />
                  <input value={buyer.state} onChange={e => setBuyer(b => ({ ...b, state: e.target.value }))}
                    placeholder="State"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Reference / Dispatch */}
          <details className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 group">
            <summary className="cursor-pointer text-zinc-300 text-sm font-semibold select-none flex items-center gap-2">
              <Truck size={13} className="text-zinc-500" /> Reference & Dispatch Details
              <span className="text-zinc-600 text-[10px] ml-auto group-open:hidden">(click to expand)</span>
            </summary>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                ['Dispatch Through', dispatchThrough, setDispatchThrough],
                ['Destination', destination, setDestination],
                ['Despatch No.', despatchNo, setDespatchNo],
                ['Delivery Note / LR No.', deliveryNote, setDeliveryNote],
                ["Supplier's Ref.", supplierRef, setSupplierRef],
                ['Other Reference(s)', otherRef, setOtherRef],
                ['Terms of Delivery', termsOfDelivery, setTermsOfDelivery],
              ].map(([label, v, setter]) => (
                <div key={label}>
                  <label className="text-zinc-500 text-[10px] mb-1 block">{label}</label>
                  <input value={v} onChange={e => setter(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-zinc-200 text-xs focus:outline-none focus:border-amber-500" />
                </div>
              ))}
            </div>
          </details>

          {/* Line Items */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
              <Package size={13} className="text-amber-400" />
              <h3 className="text-zinc-200 text-sm font-semibold">Line Items</h3>
              <span className="text-zinc-600 text-[10px]">{items.length} row{items.length > 1 ? 's' : ''} · auto-deducts FG / RM stock on save</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]" data-testid="invoice-items-table">
                <thead>
                  <tr className="bg-zinc-950 text-zinc-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left px-3 py-2 w-10">#</th>
                    <th className="text-left px-3 py-2 w-24">Product ID</th>
                    <th className="text-left px-3 py-2">Product (FG or Raw Material)</th>
                    <th className="text-right px-3 py-2 w-24">Quantity</th>
                    <th className="text-right px-3 py-2 w-20">Unit</th>
                    <th className="text-right px-3 py-2 w-28">Rate (₹)</th>
                    <th className="text-right px-3 py-2 w-32">Amount (₹)</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const product = products.find(p => p.id === it.product_id);
                    const stock = product?.current_stock || 0;
                    const qty = parseFloat(it.quantity) || 0;
                    const stockWarn = qty > stock;
                    return (
                      <tr key={i} className="border-t border-zinc-800/60 hover:bg-zinc-800/20">
                        <td className="px-3 py-2 text-zinc-600 font-mono">{i + 1}</td>
                        <td className="px-3 py-2">
                          <ProductCodeBadge name={it.product_name} code={it.product_code} />
                          {!it.product_name && <span className="text-zinc-700 text-[10px] italic">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <ProductCombobox
                            products={products}
                            value={it.product_id || ''}
                            onChange={(pid) => selectProduct(i, pid)}
                            groupByType
                            placeholder="Type product…"
                            testId={`product-select-${i}`}
                          />
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {it.has_custom_price && (
                              <span className="text-[9px] text-blue-400">Custom rate applied</span>
                            )}
                            {/* When a buyer is selected AND the picked product
                                isn't in their price-list, surface a one-click
                                "Add to price list" so the operator can save
                                the negotiated rate without leaving the bill.
                                iter202 — derive item_type from the row instead
                                of hardcoding 'finished' so RMs save correctly
                                and auto-fill on the next bill. */}
                            {buyerId && it.product_id && !it.has_custom_price && (
                              <AddToPriceListButton
                                clientId={buyerId}
                                product={{
                                  product_id: it.product_id,
                                  product_name: it.product_name,
                                  item_type: it.item_type || 'finished',
                                  unit: it.unit,
                                  rate: it.rate,
                                }}
                                currentList={clientPriceList}
                                onAdded={(entry) => setClientPriceList(prev => [...prev, entry])}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="0" step="any" value={it.quantity}
                            onChange={e => updateItem(i, 'quantity', e.target.value)}
                            onKeyDown={e => {
                              // Enter on the quantity cell adds a new line item
                              // so operators can keep one-hand billing without
                              // reaching for the "+ Add another item" button.
                              if (e.key === 'Enter' && parseFloat(it.quantity) > 0 && it.product_id) {
                                e.preventDefault();
                                addItemRow();
                              }
                            }}
                            data-testid={`qty-input-${i}`}
                            className={`w-full text-right bg-zinc-800 border rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none ${stockWarn ? 'border-red-700 text-red-400 focus:border-red-500' : 'border-zinc-700 text-zinc-50 focus:border-amber-500'}`} />
                          {it.product_id && (
                            <div className={`text-[9px] mt-0.5 text-right ${stockWarn ? 'text-red-400' : 'text-zinc-600'}`}>
                              stock: {stock} {stockWarn ? '(insufficient!)' : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <select value={it.unit} onChange={e => updateItem(i, 'unit', e.target.value)}
                            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-300 text-xs focus:outline-none">
                            {['kg', 'g', 'packs', 'pcs', 'box', 'pkt', 'mt'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="0" step="any" value={it.rate}
                            onChange={e => updateItem(i, 'rate', e.target.value)}
                            data-testid={`rate-input-${i}`}
                            className="w-full text-right bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-amber-400 text-xs font-mono focus:outline-none focus:border-amber-500" />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-sm text-amber-400 font-semibold">
                          {it.amount ? fmt(it.amount) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {items.length > 1 && (
                            <button onClick={() => removeItemRow(i)} className="text-zinc-600 hover:text-red-400">
                              <X size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-zinc-700 bg-zinc-950">
                    <td colSpan={2} className="px-3 py-2.5">
                      <button onClick={addItemRow} data-testid="add-row-btn"
                        className="text-blue-400 text-xs flex items-center gap-1 hover:text-blue-300"><Plus size={10} /> Add another item</button>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-300">
                      <span className="text-zinc-600 text-[10px] mr-2">Total Qty:</span>{totalQty ? fmt(totalQty) : '—'}
                    </td>
                    <td colSpan={2} className="px-3 py-2.5 text-right text-zinc-300 text-xs font-semibold">SUBTOTAL</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm font-semibold text-zinc-200">
                      ₹{fmt(subtotal)}
                    </td>
                    <td />
                  </tr>
                  {/* Extra-charges rows (transport, packing, loading, etc.) */}
                  {extraCharges.map((c, idx) => (
                    <tr key={idx} className="border-t border-zinc-800 bg-zinc-900/40" data-testid={`extra-charge-row-${idx}`}>
                      <td colSpan={2} className="px-3 py-1.5">
                        <input value={c.label}
                          onChange={e => setExtraCharges(prev => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                          placeholder="e.g. Transport / Packing / Loading"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1 text-zinc-100 text-xs focus:outline-none focus:border-amber-500"
                          data-testid={`extra-label-${idx}`} />
                      </td>
                      <td colSpan={3} className="px-3 py-1.5 text-right text-zinc-500 text-[10px]">Extra Charge</td>
                      <td className="px-3 py-1.5 text-right">
                        <input type="number" step="any" min="0" value={c.amount}
                          onChange={e => setExtraCharges(prev => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                          placeholder="0"
                          className="w-24 bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1 text-zinc-100 text-xs font-mono text-right focus:outline-none focus:border-amber-500"
                          data-testid={`extra-amount-${idx}`} />
                      </td>
                      <td className="px-1 text-center">
                        <button onClick={() => setExtraCharges(prev => prev.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-red-400" title="Remove charge"><X size={12} /></button>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-zinc-800">
                    <td colSpan={6} className="px-3 py-2">
                      <div className="flex flex-wrap gap-1.5">
                        <span className="text-zinc-600 text-[10px] mr-1 self-center">+ Quick add:</span>
                        {['Transport', 'Packing', 'Loading', 'Unloading', 'Custom'].map(l => (
                          <button key={l} onClick={() => setExtraCharges(prev => [...prev, { label: l === 'Custom' ? '' : l, amount: '' }])}
                            data-testid={`add-extra-${l.toLowerCase()}`}
                            className="text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 px-2 py-0.5 rounded transition-colors">
                            {l}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td />
                  </tr>
                  {/* Grand total — items + extras */}
                  <tr className="border-t-2 border-amber-500/30 bg-zinc-950">
                    <td colSpan={5} className="px-3 py-2.5 text-right text-amber-400 text-xs font-bold uppercase tracking-wider">Grand Total</td>
                    <td className="px-3 py-2.5 text-right font-mono text-lg font-bold text-amber-400" data-testid="grand-total">
                      ₹{fmt(grandTotal)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Amount in Words — prints on invoice */}
            {grandTotal > 0 && (
              <div className="px-4 py-3 border-t border-zinc-800 bg-amber-500/5" data-testid="amount-in-words">
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider mr-2">Amount Chargeable (in words):</span>
                <span className="text-amber-300 font-heading font-semibold text-sm">Rupees {amountToWords(grandTotal)}</span>
              </div>
            )}
          </div>

          {/* Narration */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider">Narration / Notes</label>
            <input value={narration} onChange={e => setNarration(e.target.value)}
              placeholder="Brief description of this transaction…"
              className="mt-2 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-200 text-sm focus:outline-none focus:border-amber-500" />
          </div>

          {/* Stock-debt warning — non-blocking inline banner so the operator
              can see exactly what shortages will be recorded BEFORE saving. */}
          {(() => {
            const validItems = items.filter(it => (it.product_name || it.product_id) && parseFloat(it.quantity) > 0);
            const shortages = validItems.map(it => {
              const p = products.find(pr => pr.id === it.product_id || pr.name === it.product_name);
              const stock = parseFloat(p?.current_stock || 0);
              const qty = parseFloat(it.quantity || 0);
              if (qty > stock) return { name: p?.name || it.product_name, shortfall: +(qty - stock).toFixed(2), unit: p?.unit || 'kg' };
              return null;
            }).filter(Boolean);
            if (shortages.length === 0) return null;
            return (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 mt-3 text-xs" data-testid="stock-debt-warning">
                <div className="font-semibold text-amber-300 mb-1.5">
                  ⚠ {shortages.length} item{shortages.length > 1 ? 's' : ''} short — saving will create stock-debt record{shortages.length > 1 ? 's' : ''}
                </div>
                <div className="text-amber-200 space-y-0.5">
                  {shortages.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-red-400">●</span>
                      <span className="text-zinc-300">{s.name}</span>
                      <span className="text-zinc-500">— short by</span>
                      <span className="font-mono text-red-300">{s.shortfall} {s.unit}</span>
                    </div>
                  ))}
                </div>
                <div className="text-amber-400/70 mt-2 text-[10.5px]">
                  Bill will save anyway; debts auto-fulfill from the next production batch of each product (visible under Stock Debts on the dashboard).
                </div>
              </div>
            );
          })()}

          {/* Action bar */}
          <div className="sticky bottom-0 bg-zinc-950/95 backdrop-blur border border-zinc-800 rounded-xl p-4 flex items-center gap-3 flex-wrap">
            <div className="text-zinc-400 text-xs">
              Total: <span className="font-mono font-bold text-lg text-amber-400">₹{fmt(grandTotal)}</span>
              <span className="text-zinc-600 ml-3">{items.filter(it => it.product_id && parseFloat(it.quantity) > 0).length} items{extraCharges.filter(c => c.label && parseFloat(c.amount) > 0).length > 0 ? ` + ${extraCharges.filter(c => c.label && parseFloat(c.amount) > 0).length} extra` : ''}</span>
              <span className="text-green-500 ml-3 text-[10px]">●  Auto-saving</span>
            </div>
            <div className="flex-1" />
            {editBill && (
              <button onClick={cancelEdit} data-testid="cancel-edit-btn"
                className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs font-medium">
                Cancel Edit
              </button>
            )}
            {!editBill && (
              <button onClick={resetForm} data-testid="reset-form-btn"
                className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs font-medium">Reset</button>
            )}
            <button onClick={handleSubmit} disabled={submitting} data-testid="save-invoice-btn"
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50">
              {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {editBill ? `Save Changes${editBill.invoice_number ? ' · ' + editBill.invoice_number : ''}` : 'Save & Generate Non-GST Invoice'}
            </button>
          </div>
        </div>
      )}

      {/* ══ BILL HISTORY TAB ══ */}
      {tab === 'history' && (
        <EnterpriseBillHistoryTab
          bills={bills}
          filteredBills={filteredBills}
          searchBill={searchBill}
          setSearchBill={setSearchBill}
          filterPayment={filterPayment}
          setFilterPayment={setFilterPayment}
          billSortKey={billSortKey}
          billSortDir={billSortDir}
          toggleBillSort={toggleBillSort}
          isAdmin={isAdmin}
          fetchData={fetchData}
          setPayBill={setPayBill}
          setShareBill={setShareBill}
          setPreviewBill={setPreviewBill}
          loadBillForEdit={loadBillForEdit}
          requestDelete={requestDelete}
        />
      )}

      {/* ══ DISPATCH REGISTER TAB ══ */}
      {tab === 'dispatch' && (
        <EnterpriseDispatchTab
          dispatch={dispatch}
          filteredDispatch={filteredDispatch}
          searchDispatch={searchDispatch}
          setSearchDispatch={setSearchDispatch}
          filterInvType={filterInvType}
          setFilterInvType={setFilterInvType}
        />
      )}

      {payBill && <PaymentModal bill={payBill} onClose={() => setPayBill(null)} onSaved={fetchData} />}
      {previewBill && <BillPrintPreview orderId={previewBill.id} onClose={() => setPreviewBill(null)} />}
      {/* EditBillModal is no longer used — Edit happens IN-PAGE via
          loadBillForEdit(bill) which hydrates the create-tab form and
          flips the Save button label to "Save Changes". The send /
          payments / attachments actions live on the row itself
          (FileText / Pay / Print buttons). */}
      {shareBill && <InvoiceModal order={shareBill} onClose={() => setShareBill(null)} onUpdate={fetchData} />}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}
